'use client'

import { useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/navigation'
import Link from 'next/link';
import {
  ShoppingCart,
  Star,
  Flame,
  Truck,
  ShieldCheck,
  Clock,
  Plus,
  Eye,
  Pencil,
  Copy,
  Link2,
  DollarSign,
  ArrowLeft,
  Unlock,
  RefreshCw,
  CalendarCheck,
  SlidersHorizontal,
  Search,
  Trophy,
  GraduationCap,
  Repeat,
  CalendarDays,
  FileDown,
  Handshake,
  Package,
  Layers,
  Trash2,
  AlertTriangle,
  Settings2,
} from 'lucide-react'

import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '@/components/ui/tooltip'

import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useSellerId } from '@/hooks/useSellerId'
import { useIsOwner } from '@/hooks/useIsOwner'
import { resolveImageUrl } from '@/lib/media/resolveImage'
import { useIsAdmin } from '@/hooks/useIsAdmin'
import { useMoney } from '@/lib/currency/useMoney'
import {
  ProductDrawer,
  type ProductForm,
  type OfferPresetId,
  type ProductType,
  type PricingType,
} from '@/components/products/ProductDrawer'
import { useSellerFeatures } from '@/hooks/useSellerFeatures'
import { cn } from '@/lib/utils'

type OfferFilter = 'all' | 'unassigned' | string

interface OfferItem {
  id: number
  name: string
  description: string
  meta_description: string
  image: string | null
  price: number
  mtaSchemeId?: number | null
  mta_scheme_id?: number | null
  isMembership?: boolean | 0 | 1
  is_membership?: boolean | 0 | 1
  membershipExpiresIn?: number | string | null
  membership_expires_in?: number | string | null
  membershipExpiresType?: 'day' | 'week' | 'month' | 'year' | string | null
  membership_expires_type?: 'day' | 'week' | 'month' | 'year' | string | null
  originalPrice?: number | null // déjalo temporal por compatibilidad
  specialPrice?: number | null
  specialStart?: string | null
  specialEnd?: string | null
  promoEnd?: string | null
  special?: number | null
  special_base?: number | null
  specialUSD?: number | null
  productType?: ProductType | string
  product_type?: ProductType | string
  pricingType?: PricingType | string
  pricing_type?: PricingType | string
  rating?: number
  reviews?: number
  category?: string
  badge?: string
  badgeType?: 'hot' | 'new' | 'sale'
  sold?: number
  earnings?: number
  status: 0 | 1
  isPublished: 0 | 1
  canView?: 0 | 1 | boolean
  can_view?: 0 | 1
  model: string
  type: OfferPresetId
  currency: string
  salesCount: number
  earningsTotal: number
  accessSummary: {
    courses: number
    challenges: number
    events: number
    downloads: number
  }
}

const productTypePresets: Array<{
  id: ProductType
  title: string
  description: string
  icon: any
  filterLabel?: string
  defaultPricingType: PricingType
  enabled?: boolean
}> = [
  {
    id: 'challenge',
    title: 'Reto / desafío',
    filterLabel: 'Retos',
    description: 'Ideal para retos de varios días, comunidad, checkpoints o experiencias guiadas.',
    icon: Trophy,
    defaultPricingType: 'one_time',
    enabled: false,
  },
  {
    id: 'course',
    title: 'Curso',
    filterLabel: 'Cursos',
    description: 'Vende acceso a lecciones, módulos o contenido educativo.',
    icon: GraduationCap,
    defaultPricingType: 'one_time',
    enabled: false,
  },
  {
    id: 'membership',
    title: 'Membresía',
    filterLabel: 'Membresías',
    description: 'Acceso recurrente a comunidad, contenido, beneficios o suscripción mensual.',
    icon: Repeat,
    defaultPricingType: 'subscription',
    enabled: false,
  },
  {
    id: 'event',
    title: 'Evento',
    filterLabel: 'Eventos',
    description: 'Webinar, taller, masterclass, evento presencial o sesión en vivo.',
    icon: CalendarDays,
    defaultPricingType: 'one_time',
    enabled: false,
  },
  {
    id: 'digital',
    title: 'Producto digital',
    filterLabel: 'Productos Digitales',
    description: 'Plantillas, archivos, descargables, recursos o contenido bajo demanda.',
    icon: FileDown,
    defaultPricingType: 'one_time',
  },
  {
    id: 'service',
    title: 'Servicio',
    filterLabel: 'Servicios',
    description: 'Coaching, consultoría, asesoría, sesiones 1 a 1 o servicios personalizados.',
    icon: Handshake,
    defaultPricingType: 'one_time',
  },
  {
    id: 'physical',
    title: 'Producto físico',
    filterLabel: 'Productos Físicos',
    description: 'Producto con inventario, envío o entrega física.',
    icon: Package,
    defaultPricingType: 'one_time',
  },
  {
    id: 'bundle',
    title: 'Bundle / paquete',
    filterLabel: 'Paquetes',
    description: 'Combina cursos, retos, eventos, descargables o beneficios en una sola oferta.',
    icon: Layers,
    defaultPricingType: 'one_time',
    enabled: false,
  },
  {
    id: 'custom',
    title: 'Personalizado',
    filterLabel: 'Personalizados',
    description: 'Oferta flexible para casos especiales.',
    icon: Settings2,
    defaultPricingType: 'custom',
    enabled: false,
  },
]

function makeDefaultProductForm(type: OfferPresetId = 'custom'): ProductForm {
  return {
    productId: null,
    type,

    name: '',
    description: '',
    metaDescription: '',
    metaTitle: '',
    metaKeyword: '',
    tag: '',
    keyword: '',
    model: '',

    productType: type,
    pricingType:
      type === 'membership'
        ? 'subscription'
        : type === 'custom'
          ? 'custom'
          : 'one_time',

    image: '',
    video: '',
    gallery: [],

    price: '',
    quantity: '999',
    minimum: '1',
    currency: 'USD',
    status: true,
    isPublished: false,
    installments: false,

    recurringIds: [],
    recurringEnabled: type === 'subscription',
    recurringInterval: 'month',
    recurringIntervalCount: '1',
    trialEnabled: false,
    trialDays: '',

    mtaSchemeId: null,

    isMembership: false,
    membershipExpiresIn: '1',
    membershipExpiresType: 'year',

    promoEnabled: false,
    promoType: 'date',
    promoPrice: '',
    promoDateStart: '',
    promoDateEnd: '',
    promoDays: '',

    duration: type === 'event' ? 'limited' : 'forever',
    dateAvailable: '',
    dateStart: '',
    timeStart: '',
    dateEnd: '',
    timeEnd: '',
    location: '',

    expiresEnabled: false,
    expiresIn: '',
    expiresType: 'month',

    accessCourseIds: [],
    accessChallengeIds: [],
    accessEventIds: [],
    accessDownloadIds: [],
    accessProductIds: [],
    canClone: false,
    canPromote: false,
    sortOrder: '0',

    upsells: [],

    thankyou: {
      title: '¡Gracias por tu compra!',
      message: '',
      video: '',
      action: 'home',
      link: '',
      button: 'Continuar',
      status: false,
    },
  }
}
function cleanLegacyDate(value: any) {
  if (!value) return ''

  const str = String(value)

  // OpenCart 0000-00-00 suele llegar como 1899-11-30T00:00:00.000Z
  if (
    str.startsWith('0000-00-00') ||
    str.startsWith('1899-11-30')
  ) {
    return ''
  }

  if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
    return str.slice(0, 10)
  }

  return ''
}

function cleanLegacyTime(value: any) {
  if (!value) return ''

  const str = String(value)

  if (
    str.startsWith('0000-00-00') ||
    str.startsWith('1899-11-30')
  ) {
    return ''
  }

  const match = str.match(/T?(\d{2}:\d{2})/)
  return match?.[1] || ''
}

function mapProductDetailToForm(
  product: any,
  fallbackType: OfferPresetId = 'custom'
): ProductForm {
  const rawType =
    product?.productType ||
    product?.product_type ||
    product?.type ||
    fallbackType ||
    'custom'

  const type = (
    rawType === 'subscription'
      ? 'membership'
      : rawType === 'one-time'
        ? 'custom'
        : rawType
  ) as OfferPresetId

  const specialPrice =
    product?.specialPrice ??
    product?.special_price ??
    product?.promoPrice ??
    product?.promo_price ??
    product?.special ??
    product?.special_base ??
    null

  const specialStart =
    product?.specialStart ??
    product?.special_start ??
    product?.promoDateStart ??
    product?.promo_date_start ??
    null

  const specialEnd =
    product?.specialEnd ??
    product?.special_end ??
    product?.promoDateEnd ??
    product?.promo_date_end ??
    null

  const specialType =
    product?.promoType ??
    product?.promo_type ??
    product?.specialType ??
    product?.special_type ??
    product?.type_special ??
    'date'

  const specialDays =
    product?.promoDays ??
    product?.promo_days ??
    product?.specialDays ??
    product?.special_days ??
    product?.days ??
    ''

  return {
    ...makeDefaultProductForm(type),

    productId: Number(product?.id || product?.product_id || 0) || null,
    type,
    productType: type,
    pricingType: (
      product?.pricingType ||
      product?.pricing_type ||
      (type === 'membership' ? 'subscription' : 'one_time')
    ) as ProductForm['pricingType'],

    name: String(product?.name || ''),
    description: String(product?.description || ''),
    metaDescription: String(product?.meta_description || product?.metaDescription || ''),
    metaTitle: String(product?.meta_title || product?.metaTitle || ''),
    metaKeyword: String(product?.meta_keyword || product?.metaKeyword || ''),
    tag: String(product?.tag || ''),
    keyword: String(product?.keyword || ''),
    model: String(product?.model || ''),

    image: String(product?.image || product?.main_image || ''),
    video: String(product?.videoUrl || product?.video_url || product?.video || ''),
    gallery: Array.isArray(product?.gallery)
      ? product.gallery
      : Array.isArray(product?.images)
        ? product.images
        : [],

    price: product?.price != null ? String(product.price) : '',
    quantity: product?.quantity != null ? String(product.quantity) : '999',
    minimum: product?.minimum != null ? String(product.minimum) : '1',
    currency: String(product?.currency || 'USD'),
    status: Number(product?.status ?? 1) === 1,
    isPublished: Number(
      product?.isPublished ??
        product?.is_published ??
        product?.can_view ??
        product?.canView ??
        0
    ) === 1,
    installments: Number(product?.installments || 0) === 1,

    recurringIds: Array.isArray(product?.recurringIds)
      ? product.recurringIds.map(Number)
      : Array.isArray(product?.community_recurring_ids)
        ? product.community_recurring_ids.map(Number)
        : Array.isArray(product?.recurrings)
          ? product.recurrings
              .map((r: any) => Number(r.recurring_id || r.product_recurring_id || r.id))
              .filter((id: number) => Number.isFinite(id) && id > 0)
          : [],
          
    recurringEnabled: Boolean(
      product?.recurringEnabled ||
        product?.recurring_enabled ||
        type === 'membership' ||
        product?.pricingType === 'subscription' ||
        product?.pricing_type === 'subscription'
    ),
    recurringInterval: (
      product?.recurringInterval ||
      product?.recurring_interval ||
      'month'
    ) as ProductForm['recurringInterval'],
    recurringIntervalCount: String(
      product?.recurringIntervalCount ||
        product?.recurring_interval_count ||
        '1'
    ),
    trialEnabled: Boolean(product?.trialEnabled || product?.trial_enabled),
    trialDays: String(product?.trialDays || product?.trial_days || ''),

    mtaSchemeId:
      product?.mtaSchemeId != null
        ? Number(product.mtaSchemeId)
        : product?.mta_scheme_id != null
          ? Number(product.mta_scheme_id)
          : null,

    isMembership:
      Number(product?.is_membership ?? product?.isMembership ?? 0) === 1,

    membershipExpiresIn: String(
      product?.membership_expires_in ??
        product?.membershipExpiresIn ??
        '1'
    ),

    membershipExpiresType: (
      product?.membership_expires_type ??
        product?.membershipExpiresType ??
        'year'
    ) as ProductForm['membershipExpiresType'],

    promoEnabled: Number(specialPrice || 0) > 0,
    promoType: (specialType === 'days' ? 'days' : 'date') as ProductForm['promoType'],
    promoPrice: specialPrice != null ? String(specialPrice) : '',
    promoDateStart: cleanLegacyDate(specialStart),
    promoDateEnd: cleanLegacyDate(specialEnd),
    promoDays: String(specialDays || ''),

    duration: (product?.duration || 'forever') as ProductForm['duration'],
    dateAvailable: cleanLegacyDate(product?.dateAvailable || product?.date_available),
    dateStart: cleanLegacyDate(product?.dateStart || product?.date_start || product?.datetime_start),
    timeStart: cleanLegacyTime(product?.timeStart || product?.time_start || product?.datetime_start),
    dateEnd: cleanLegacyDate(product?.dateEnd || product?.date_end || product?.datetime_end),
    timeEnd: cleanLegacyTime(product?.timeEnd || product?.time_end || product?.datetime_end),
    location: String(product?.location || ''),

    expiresEnabled: Number(product?.product_expires_in || product?.expiresIn || 0) > 0,
    expiresIn: String(product?.product_expires_in || product?.expiresIn || ''),
    expiresType: (
      product?.product_expires_type ||
      product?.expiresType ||
      'month'
    ) as ProductForm['expiresType'],

    accessCourseIds: Array.isArray(product?.accessCourseIds)
      ? product.accessCourseIds.map(Number)
      : [],
    accessChallengeIds: Array.isArray(product?.accessChallengeIds)
      ? product.accessChallengeIds.map(Number)
      : [],
    accessEventIds: Array.isArray(product?.accessEventIds)
      ? product.accessEventIds.map(Number)
      : [],
    accessDownloadIds: Array.isArray(product?.accessDownloadIds)
      ? product.accessDownloadIds.map(Number)
      : [],
    accessProductIds: Array.isArray(product?.accessProductIds)
      ? product.accessProductIds.map(Number)
      : [],

    canClone: Number(product?.can_clone ?? product?.canClone ?? 0) === 1,
    canPromote: Number(product?.can_promote ?? product?.canPromote ?? 0) === 1,
    sortOrder: String(product?.sort_order || product?.sortOrder || '0'),

    upsells: Array.isArray(product?.upsells) ? product.upsells : [],

    thankyou: {
      title: String(
        product?.thankyou?.title ??
        '¡Gracias por tu compra!'
      ),

      message: String(
        product?.thankyou?.message ??
        ''
      ),

      video: String(
        product?.thankyou?.video ??
        ''
      ),

      action: (
        product?.thankyou?.action ||
        'home'
      ) as ProductForm['thankyou']['action'],

      link: String(
        product?.thankyou?.link ??
        ''
      ),

      button: String(
        product?.thankyou?.button ??
        'Continuar'
      ),

      status:
        Number(
          product?.thankyou?.status ??
          0
        ) === 1,
    },
  }
}

function resolveProductType(form: ProductForm) {
  const type = String(form.type || form.productType || '').trim()

  if (type === 'subscription') return 'membership'
  if (type === 'membership') return 'membership'
  if (type === 'event') return 'event'
  if (type === 'digital') return 'digital'
  if (type === 'course') return 'course'
  if (type === 'challenge') return 'challenge'
  if (type === 'service') return 'service'
  if (type === 'physical') return 'physical'
  if (type === 'bundle') return 'bundle'

  return 'custom'
}

function resolvePricingType(form: ProductForm) {
  const type = String(form.type || form.productType || '').trim()

  if (type === 'subscription' || type === 'membership') return 'subscription'
  if (form.recurringEnabled || form.recurringIds?.length > 0) return 'subscription'
  if (type === 'custom') return 'custom'

  return 'one_time'
}

function mapFormToProductPayload(form: ProductForm, sellerId: number) {
  return {
    sellerId,
    productId: form.productId,
    type: form.type,

    name: form.name.trim(),
    description: form.description,
    meta_description: form.metaDescription,
    meta_title: form.metaTitle,
    meta_keyword: form.metaKeyword,
    tag: form.tag,
    keyword: form.keyword,
    model: form.model,

    image: form.image,
    video: form.video,
    gallery: form.gallery
      .filter((item) => item?.image)
      .filter((item) => item.image !== form.image)
      .filter((item, index, arr) => {
        return arr.findIndex((x) => x.image === item.image) === index
      })
      .map((item, index) => ({
        image: item.image,
        sortOrder: index + 1,
      })),

    price: Number(form.price || 0),
    quantity: Number(form.quantity || 0),
    minimum: Number(form.minimum || 1),
    currency: form.currency,
    status: form.status ? 1 : 0,
    isPublished: form.isPublished ? 1 : 0,
    is_published: form.isPublished ? 1 : 0,

    can_view: form.isPublished ? 1 : 0,
    canView: form.isPublished ? 1 : 0,
    can_clone: form.canClone ? 1 : 0,
    can_promote: form.canPromote ? 1 : 0,
    sort_order: Number(form.sortOrder || 0),
    installments: form.installments ? 1 : 0,

    recurringIds: form.recurringIds,
    community_recurring_ids: form.recurringIds,
    recurringEnabled: form.recurringEnabled ? 1 : 0,
    recurringInterval: form.recurringInterval,
    recurringIntervalCount: Number(form.recurringIntervalCount || 1),
    trialEnabled: form.trialEnabled ? 1 : 0,
    trialDays: Number(form.trialDays || 0),

    mta_scheme_id: form.mtaSchemeId || 0,
    mtaSchemeId: form.mtaSchemeId || 0,

    is_membership: form.isMembership ? 1 : 0,
    isMembership: form.isMembership ? 1 : 0,
    membership_expires_in: form.isMembership
      ? Number(form.membershipExpiresIn || 1)
      : 0,
    membershipExpiresIn: form.isMembership
      ? Number(form.membershipExpiresIn || 1)
      : 0,
    membership_expires_type: form.isMembership
      ? form.membershipExpiresType
      : '',
    membershipExpiresType: form.isMembership
      ? form.membershipExpiresType
      : '',

    promoEnabled: form.promoEnabled ? 1 : 0,
    promoType: form.promoType,
    promoPrice: Number(form.promoPrice || 0),
    promoDateStart: form.promoDateStart,
    promoDateEnd: form.promoDateEnd,
    promoDays: Number(form.promoDays || 0),

    duration: form.duration,
    date_available: form.dateAvailable,
    date_start: form.duration === 'limited' ? form.dateStart : '',
    time_start: form.duration === 'limited' ? form.timeStart : '',
    date_end: form.duration === 'limited' ? form.dateEnd : '0000-00-00',
    time_end: form.duration === 'limited' ? form.timeEnd : '',
    location: form.location,

    product_expires_in: form.expiresEnabled ? Number(form.expiresIn || 0) : 0,
    product_expires_type: form.expiresType,

    accessCourseIds: form.accessCourseIds,
    accessChallengeIds: form.accessChallengeIds,
    accessEventIds: form.accessEventIds,
    accessDownloadIds: form.accessDownloadIds,
    accessProductIds: form.accessProductIds,

    product_type: resolveProductType(form),
    pricing_type: resolvePricingType(form),

    upsells: form.upsells,

    thankyou: {
      title: form.thankyou.title.trim() || '¡Gracias por tu compra!',
      message: form.thankyou.message || '',
      video: form.thankyou.video.trim(),
      action: form.thankyou.action,
      link:
        form.thankyou.action === 'link'
          ? form.thankyou.link.trim()
          : '',
      button: form.thankyou.button.trim() || 'Continuar',
      status: form.thankyou.status ? 1 : 0,
    },
  }
}

const ownerFilters: Array<{ id: OfferFilter; label: string }> = [
  { id: 'all', label: 'Todas' },
  ...productTypePresets
    .filter((preset) => preset.enabled !== false)
    .map((preset) => ({
      id: preset.id,
      label: preset.filterLabel || preset.title,
    })),
  { id: 'unassigned', label: 'Sin asignar' },
]

function getOfferTypeLabel(type: OfferPresetId) {
  switch (type) {
    case 'subscription':
      return 'Suscripción'
    case 'event':
      return 'Evento de pago'
    case 'digital':
      return 'Producto digital'
    case 'custom':
      return 'Personalizada'
    default:
      return 'Pago único'
  }
}

export function htmlToPlainText(html?: string | null) {
  if (!html) return ''

  const div = document.createElement('div')
  div.innerHTML = html
  return (div.textContent || div.innerText || '').trim()
}

function getPublicCategory(item: OfferItem) {
  const accessSummary = item.accessSummary || {
    courses: 0,
    challenges: 0,
    events: 0,
    downloads: 0,
  }

  if (item.category) return item.category
  if (accessSummary.downloads > 0) return 'Descargables'
  if (accessSummary.events > 0) return 'Eventos'
  if (accessSummary.challenges > 0) return 'Retos'
  if (accessSummary.courses > 0) return 'Cursos'
  if (item.type === 'membership') return 'Membresía'
  if (item.type === 'digital') return 'Producto digital'
  return 'Oferta'
}

function safeImg(src?: string | null) {
  const siteOrigin =
    typeof window !== 'undefined' ? window.location.origin : ''

  return resolveImageUrl(src, {
    siteOrigin,
    assetBaseUrl: process.env.NEXT_PUBLIC_BASE_URL || '',
  })
}

function getItemProductType(item: OfferItem) {
  return String(
    item.productType ||
      item.product_type ||
      item.type ||
      'custom'
  )
}

function getAccessBadges(item: OfferItem) {
  const out: string[] = []
  if (item.accessSummary.courses > 0) out.push(`Curso${item.accessSummary.courses > 1 ? 's' : ''}`)
  if (item.accessSummary.challenges > 0) out.push(`Reto${item.accessSummary.challenges > 1 ? 's' : ''}`)
  if (item.accessSummary.events > 0) out.push(`Evento${item.accessSummary.events > 1 ? 's' : ''}`)
  if (item.accessSummary.downloads > 0) out.push(`Descargable${item.accessSummary.downloads > 1 ? 's' : ''}`)
  return out
}

function CountdownTimer({ targetDate }: { targetDate: string }) {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 })

  useEffect(() => {
    const calc = () => {
      const diff = +new Date(targetDate) - +new Date()
      if (diff > 0) {
        setTimeLeft({
          days: Math.floor(diff / (1000 * 60 * 60 * 24)),
          hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((diff / 1000 / 60) % 60),
          seconds: Math.floor((diff / 1000) % 60),
        })
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 })
      }
    }

    calc()
    const t = setInterval(calc, 1000)
    return () => clearInterval(t)
  }, [targetDate])

  const units = [
    { value: timeLeft.days, label: 'días' },
    { value: timeLeft.hours, label: 'hrs' },
    { value: timeLeft.minutes, label: 'min' },
    { value: timeLeft.seconds, label: 'seg' },
  ]

  return (
    <div className="flex gap-2">
      {units.map((u) => (
        <div
          key={u.label}
          className="flex min-w-[48px] flex-col items-center rounded-lg bg-destructive/10 px-2.5 py-1.5"
        >
          <span className="tabular-nums text-lg font-bold text-destructive">
            {String(u.value).padStart(2, '0')}
          </span>
          <span className="text-[10px] font-medium uppercase text-destructive/70">{u.label}</span>
        </div>
      ))}
    </div>
  )
}

function StarRating({ rating = 4.8, reviews = 0 }: { rating?: number; reviews?: number }) {
  return (
    <div className="flex items-center gap-1.5">
      <div className="flex">
        {[1, 2, 3, 4, 5].map((i) => (
          <Star
            key={i}
            className={`h-3.5 w-3.5 ${
              i <= Math.round(rating) ? 'fill-accent text-accent' : 'text-muted-foreground/30'
            }`}
          />
        ))}
      </div>
      <span className="text-xs text-muted-foreground">
        {rating.toFixed(1)} ({reviews})
      </span>
    </div>
  )
}

function BadgeTag({ text, type }: { text: string; type?: 'hot' | 'new' | 'sale' }) {
  const styles = {
    hot: 'bg-destructive text-destructive-foreground',
    new: 'bg-primary text-primary-foreground',
    sale: 'bg-accent text-accent-foreground',
  }

  return (
    <span
      className={`absolute left-3 top-3 z-10 rounded-md px-2.5 py-1 text-xs font-bold shadow-md ${
        styles[type || 'new']
      }`}
    >
      {text}
    </span>
  )
}

function firstPositiveNumber(...values: any[]) {
  for (const value of values) {
    const num = Number(value)
    if (Number.isFinite(num) && num > 0) return num
  }

  return 0
}

function getOfferPricing(product: OfferItem) {
  const rawPrice = Number(product.price || 0)
  const rawOriginalPrice = Number(product.originalPrice || 0)

  const rawSpecialPrice = firstPositiveNumber(
    product.specialPrice,
    (product as any).special,
    (product as any).special_base,
    (product as any).specialUSD
  )

  // Caso moderno:
  // price = normal, specialPrice/special = promo
  if (
    rawSpecialPrice > 0 &&
    rawPrice > 0 &&
    rawSpecialPrice < rawPrice
  ) {
    return {
      hasSpecial: true,
      displayPrice: rawSpecialPrice,
      crossedPrice: rawPrice,
      discount: Math.round(((rawPrice - rawSpecialPrice) / rawPrice) * 100),
    }
  }

  // Caso legacy/listado viejo:
  // price = promo, originalPrice = normal
  if (
    rawOriginalPrice > 0 &&
    rawPrice > 0 &&
    rawPrice < rawOriginalPrice
  ) {
    return {
      hasSpecial: true,
      displayPrice: rawPrice,
      crossedPrice: rawOriginalPrice,
      discount: Math.round(((rawOriginalPrice - rawPrice) / rawOriginalPrice) * 100),
    }
  }

  // Caso raro:
  // price = promo, price_base = normal
  const priceBase = Number((product as any).price_base || 0)

  if (
    priceBase > 0 &&
    rawPrice > 0 &&
    rawPrice < priceBase
  ) {
    return {
      hasSpecial: true,
      displayPrice: rawPrice,
      crossedPrice: priceBase,
      discount: Math.round(((priceBase - rawPrice) / priceBase) * 100),
    }
  }

  return {
    hasSpecial: false,
    displayPrice: rawPrice,
    crossedPrice: null,
    discount: 0,
  }
}

function PromoCard({ product }: { product: OfferItem }) {
  const router = useRouter()
  const { money } = useMoney()
  const { displayPrice, crossedPrice, discount } = getOfferPricing(product)

  return (
    <Card className="col-span-full overflow-hidden border-2 border-destructive/20 bg-gradient-to-br from-destructive/5 via-card to-card">
      <CardContent className="p-0">
        <div className="grid gap-0 md:grid-cols-2">
          <div className="relative h-56 overflow-hidden md:h-auto">
            {product.image ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={safeImg(product.image)} alt={product.name} className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-muted">
                <Package className="h-10 w-10 text-muted-foreground" />
              </div>
            )}

            <div className="absolute inset-0 bg-gradient-to-t from-foreground/40 to-transparent md:bg-gradient-to-r" />

            <div className="absolute left-3 top-3 flex items-center gap-2">
              <span className="flex items-center gap-1 rounded-md bg-destructive px-3 py-1.5 text-sm font-bold text-destructive-foreground shadow-lg">
                <Flame className="h-4 w-4" /> {discount}% OFF
              </span>
            </div>
          </div>

          <div className="flex flex-col justify-center gap-4 p-6 md:p-8">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="border-destructive/30 text-destructive">
                <Clock className="mr-1 h-3 w-3" /> Oferta limitada
              </Badge>
              <Badge variant="secondary">{getPublicCategory(product)}</Badge>
            </div>

            <h2 className="text-2xl font-bold leading-tight text-foreground md:text-3xl">
              {product.name}
            </h2>

            <p className="leading-relaxed text-muted-foreground">
              {product.meta_description || 'Descubre esta oferta disponible por tiempo limitado.'}
            </p>

            {product.promoEnd ? <CountdownTimer targetDate={product.promoEnd} /> : null}

            <div className="mt-1 flex items-end gap-3">
              <span className="text-3xl font-extrabold text-foreground">
                {money(displayPrice, product.currency)}
              </span>

              {crossedPrice ? (
                <span className="mb-0.5 text-lg text-muted-foreground line-through">
                  {money(crossedPrice, product.currency)}
                </span>
              ) : null}
            </div>
            <Button
            size="lg"
            className="mt-1 w-full md:w-auto"
            onClick={() => router.push(`/product/${product.id}`)}
            >
            <ShoppingCart className="mr-2 h-5 w-5" />
            Comprar ahora
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

function ProductCard({ product }: { product: OfferItem }) {
  const router = useRouter()
  const { money } = useMoney()
  const { isAdmin, loading: adminLoading } = useIsAdmin()
  const { displayPrice, crossedPrice, discount, hasSpecial } =
    getOfferPricing(product)
  return (
    <Card className="group flex flex-col overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-md">
      <div className="relative overflow-hidden px-2 pt-2">
        <div className="aspect-video w-full overflow-hidden rounded-xl">
          {product.badge ? <BadgeTag text={product.badge} type={product.badgeType} /> : null}
          {product.isPublished !== 1 ? (
            <span className="absolute right-3 top-3 z-10 rounded-md bg-muted px-2.5 py-1 text-xs font-bold text-muted-foreground shadow-md">
              Borrador
            </span>
          ) : null}

          {product.image ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={safeImg(product.image)}
              alt={product.name}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-muted">
              <Package className="h-8 w-8 text-muted-foreground" />
            </div>
          )}
        </div>
      </div>

      <CardContent className="flex flex-1 flex-col gap-2.5 p-5">
        {!adminLoading && isAdmin && (
        <Badge variant="outline" className="w-fit text-xs">
          {getPublicCategory(product)}
        </Badge>
        )}

        <h3 className="line-clamp-2 font-semibold leading-snug text-foreground">{product.name}</h3>

        <p className="line-clamp-2 flex-1 text-sm text-muted-foreground">
          {htmlToPlainText(product?.meta_description) ||
            ''}
        </p>

        {!adminLoading && isAdmin && (
        <StarRating rating={product.rating || 4.8} reviews={product.reviews || product.salesCount || 0} />
        )}

        <div className="mt-auto flex flex-col gap-2 border-t border-border pt-2">
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="text-xl font-bold text-foreground">
              {money(displayPrice, product.currency)}
            </span>

            {crossedPrice ? (
              <span className="text-sm text-muted-foreground line-through">
                {money(crossedPrice, product.currency)}
              </span>
            ) : null}
          </div>
          <Button
            size="sm"
            className="w-full"
            onClick={() => router.push(`/product/${product.id}`)}
            >
            <ShoppingCart className="mr-1 h-4 w-4" />
            Comprar
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

function ProductListRow({
  product,
  onToggle,
  onEdit,
  onDuplicate,
  onCopyLink,
  onDelete,
}: {
  product: OfferItem
  onToggle: (item: OfferItem, next: boolean) => void
  onEdit: (item: OfferItem) => void
  onDuplicate: (item: OfferItem) => void
  onCopyLink: (item: OfferItem) => void
  onDelete: (item: OfferItem) => void
}) {
  const [active, setActive] = useState(product.status === 1)
  const { money } = useMoney()

  const { displayPrice, crossedPrice, discount, hasSpecial } =
    getOfferPricing(product)

  useEffect(() => {
    setActive(product.status === 1)
  }, [product.status])


  const [isPublished, setIsPublished] = useState(product.isPublished === 1)

  useEffect(() => {
    setIsPublished(product.isPublished === 1)
  }, [product.isPublished])

  return (
    <Card className="group relative overflow-hidden hubee-premium-card hover:shadow-glow transition-all duration-300 animate-scale-in flex flex-col rounded-xl">
      <CardContent className="p-0">
        <div className="flex items-center gap-4 p-4">
          {product.image ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={safeImg(product.image)}
              alt={product.name}
              className="h-16 w-16 shrink-0 rounded-lg object-cover md:h-20 md:w-20"
            />
          ) : (
            <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg bg-muted md:h-20 md:w-20">
              <Package className="h-6 w-6 text-muted-foreground" />
            </div>
          )}

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h3 className="truncate font-semibold text-foreground">{product.name}</h3>

              {product.isPublished !== 1 ? (
                <Badge variant="secondary" className="text-xs">
                  Borrador
                </Badge>
              ) : null}

              {product.status !== 1 ? (
                <Badge variant="outline" className="text-xs">
                  Inactivo
                </Badge>
              ) : null}
            </div>

            <p className="line-clamp-1 text-sm text-muted-foreground">
              {product.meta_description || 'Sin descripción corta'}
            </p>
          </div>

          <div className="hidden shrink-0 text-right sm:block">
            <span className="font-bold text-foreground">
              {money(displayPrice, product.currency)}
            </span>

            {crossedPrice ? (
              <span className="block text-xs text-muted-foreground line-through">
                {money(crossedPrice, product.currency)}
              </span>
            ) : null}
          </div>

          <div className="hidden shrink-0 items-center gap-6 text-sm lg:flex">
            <div className="text-center">
              <span className="block font-semibold text-foreground">{product.salesCount ?? 0}</span>
              <span className="text-[10px] text-muted-foreground">Vendidos</span>
            </div>
            <div className="text-center">
              <span className="flex items-center justify-center gap-0.5 font-semibold text-foreground">
                <DollarSign className="h-3 w-3" />
                {Number(product.earningsTotal || 0).toLocaleString('es-MX', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
              <span className="text-[10px] text-muted-foreground">Ganancias</span>
            </div>
          </div>

          <div className="hidden shrink-0 flex-col items-center gap-1 md:flex">
            <Switch
              checked={active}
              onCheckedChange={(checked) => {
                setActive(checked)
                onToggle(product, checked)
              }}
            />
            <span className="text-[10px] text-muted-foreground">{active ? 'Activo' : 'Inactivo'}</span>
          </div>

          <TooltipProvider delayDuration={200}>
            <div className="flex shrink-0 items-center gap-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8"
                    onClick={() => onDuplicate(product)}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Clonar</TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8"
                    onClick={() => onCopyLink(product)}
                  >
                    <Link2 className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Copiar link</TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Link
                    href={`/product/${product.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-accent hover:text-accent-foreground"
                  >
                    <Eye className="h-4 w-4" />
                  </Link>
                </TooltipTrigger>
                <TooltipContent>Vista Previa</TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8"
                    onClick={() => onEdit(product)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Editar</TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                    onClick={() => onDelete(product)}
                    aria-label="Eliminar producto"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Eliminar producto</TooltipContent>
              </Tooltip>
            </div>
          </TooltipProvider>
        </div>
      </CardContent>
    </Card>
  )
}

export default function OffersPage() {
  const router = useRouter()
  const { isLoading: isUserLoading } = useCurrentUser()
  const sellerState = useSellerId()
  const { isOwner } = useIsOwner()

  const sellerId = Number((sellerState as any)?.sellerId || 0)
  const isSellerLoading = Boolean((sellerState as any)?.isLoading)

  const { features, loading } = useSellerFeatures(sellerId)

  const [affiliateSchemes, setAffiliateSchemes] = useState<
    Array<{ mta_scheme_id: number; name: string }>
  >([])

  const [items, setItems] = useState<OfferItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const { isAdmin, loading: adminLoading } = useIsAdmin()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showNewModal, setShowNewModal] = useState(false)
  const [newOfferName, setNewOfferName] = useState('')
  const [selectedType, setSelectedType] = useState<OfferPresetId | null>(null)
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<OfferFilter>('all')
  const [editMode, setEditMode] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>('create')
  const [drawerLoading, setDrawerLoading] = useState(false)
  const [drawerSaving, setDrawerSaving] = useState(false)
  const [productForm, setProductForm] = useState<ProductForm>(() =>
    makeDefaultProductForm('custom')
  )

  const [productToDelete, setProductToDelete] = useState<OfferItem | null>(null)
  const [productToClone, setProductToClone] = useState<OfferItem | null>(null)
  const [deletingProduct, setDeletingProduct] = useState(false)
  const [cloningProduct, setCloningProduct] = useState(false)

  const [sellerCurrency, setSellerCurrency] = useState('USD')

  const { money } = useMoney()

  const loadOffers = async () => {
    const sid = Number(sellerId || 0)
    if (!sid) return

    setIsLoading(true)

    try {
      const params = new URLSearchParams()
      params.set('sellerId', String(sid))

      if (isOwner && query.trim()) params.set('q', query.trim())
      if (isOwner && filter !== 'all') params.set('filter', filter)

      const res = await fetch(`/api/offers?${params.toString()}`, {
        method: 'GET',
        cache: 'no-store',
      })

      const data = await res.json().catch(() => null)
      if (!res.ok) throw new Error(data?.error || 'No se pudieron cargar las ofertas')

      setSellerCurrency(String(data?.currency || 'USD'))

      setItems(Array.isArray(data?.items) ? data.items : [])
    } catch (err) {
      console.error('[offers.load]', err)
      setItems([])
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    let cancelled = false

    async function loadAffiliateSchemes() {
      if (!features?.affiliate_invites) {
        setAffiliateSchemes([])
        return
      }

      try {
        const res = await fetch('/api/product/affiliate-schemes', {
          cache: 'no-store',
        })

        const data = await res.json().catch(() => null)

        if (!res.ok || !data?.ok) {
          throw new Error(data?.error || 'Failed to load affiliate schemes')
        }

        if (!cancelled) {
          setAffiliateSchemes(Array.isArray(data.schemes) ? data.schemes : [])
        }
      } catch {
        if (!cancelled) {
          setAffiliateSchemes([])
        }
      }
    }

    loadAffiliateSchemes()

    return () => {
      cancelled = true
    }
  }, [features?.affiliate_invites])

  useEffect(() => {
    if (!sellerId || isUserLoading || isSellerLoading) return
    loadOffers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellerId, isUserLoading, isSellerLoading, filter, isOwner])

  useEffect(() => {
    if (!isOwner) return
    if (!sellerId || isUserLoading || isSellerLoading) return

    const t = setTimeout(() => {
      loadOffers()
    }, 250)

    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query])

  const promoProduct = useMemo(() => {
    return items.find((p) => {
      const canShow = isOwner
        ? p.status === 1
        : p.status === 1 && p.isPublished === 1

      const { hasSpecial } = getOfferPricing(p)

      return canShow && hasSpecial
    })
  }, [items, isOwner])

  const excludedModels = ['DRIP']

  const regularProducts = useMemo(
    () =>
      items.filter(
        (p) =>
          p.status === 1 &&
          p.id !== promoProduct?.id &&
          !excludedModels.includes(p.model)
      ),
    [items, promoProduct]
  )

  const ownerVisibleProducts = useMemo(
    () =>
      items.filter(
        (p) =>
          p.status === 1 &&
          p.id !== promoProduct?.id &&
          !excludedModels.includes(p.model)
      ),
    [items, promoProduct]
  )

  const publicProducts = useMemo(
    () =>
      items.filter(
        (p) =>
          p.status === 1 &&
          p.isPublished === 1 &&
          p.id !== promoProduct?.id &&
          !excludedModels.includes(p.model)
      ),
    [items, promoProduct]
  )

  const visibleProductsBase = isOwner ? ownerVisibleProducts : publicProducts

  const publicCategories = useMemo(() => {
    const base = ['Todos']
    const dynamic = Array.from(
      new Set(visibleProductsBase.map((p) => getPublicCategory(p)))
    )
    return [...base, ...dynamic]
  }, [visibleProductsBase])

  const [publicCategory, setPublicCategory] = useState('Todos')

  const publicVisibleProducts = useMemo(() => {
    if (publicCategory === 'Todos') return visibleProductsBase

    return visibleProductsBase.filter(
      (p) => getPublicCategory(p) === publicCategory
    )
  }, [visibleProductsBase, publicCategory])

  const displayedProducts = useMemo(() => {
    return publicVisibleProducts.filter((p) => {
      if (!isOwner) return true

      if (filter === 'all') return true

      if (filter === 'unassigned') {
        return (
          !(p.accessSummary?.courses || 0) &&
          !(p.accessSummary?.challenges || 0) &&
          !(p.accessSummary?.events || 0) &&
          !(p.accessSummary?.downloads || 0)
        )
      }

      return getItemProductType(p) === filter
    })
  }, [publicVisibleProducts, isOwner, filter])

  const stats = useMemo(() => {
    const active = items.filter((i) => i.status === 1).length
    const inactive = items.filter((i) => i.status !== 1).length
    const isPublished = items.filter((i) => i.isPublished === 1).length
    const revenue = items.reduce((sum, i) => sum + Number(i.earningsTotal || 0), 0)
    return {
      total: items.length,
      active,
      inactive,
      revenue,
    }
  }, [items])

  const loadProductIntoDrawer = async (productId: number, fallback?: OfferItem) => {
    if (!sellerId || !productId) return

    setDrawerLoading(true)

    try {
      const params = new URLSearchParams()
      params.set('sellerId', String(sellerId))

      const res = await fetch(`/api/offers/${productId}?${params.toString()}`, {
        method: 'GET',
        cache: 'no-store',
      })

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        throw new Error(data?.error || 'No se pudo cargar el producto')
      }

      setProductForm(
        mapProductDetailToForm(
          data?.product || data?.item || data,
          fallback?.type || 'custom'
        )
      )
    } catch (err) {
      console.error('[offers.drawer.load]', err)

      if (fallback) {
        const published = Number(
          fallback.isPublished ??
            fallback.canView ??
            fallback.can_view ??
            0
        ) === 1

        setProductForm(
          mapProductDetailToForm(
            {
              id: fallback.id,
              product_id: fallback.id,
              type: fallback.type,
              name: fallback.name,
              description: fallback.description,
              meta_description: fallback.meta_description,
              image: fallback.image,
              price: fallback.price,
              specialPrice: fallback.specialPrice,
              specialStart: fallback.specialStart,
              specialEnd: fallback.specialEnd,
              status: fallback.status,

              isPublished: published ? 1 : 0,
              canView: published ? 1 : 0,
              can_view: published ? 1 : 0,

              model: fallback.model,
              currency: fallback.currency,

              is_membership: fallback.is_membership ?? fallback.isMembership ?? 0,
              membership_expires_in: fallback.membership_expires_in ?? fallback.membershipExpiresIn ?? 1,
              membership_expires_type: fallback.membership_expires_type ?? fallback.membershipExpiresType ?? 'year',
            },
            fallback.type
          )
        )
      }
    } finally {
      setDrawerLoading(false)
    }
  }

  const openCreateDrawer = (type: OfferPresetId = 'custom', name = '') => {
    setDrawerMode('create')
    setProductForm({
      ...makeDefaultProductForm(type),
      name,
    })
    setDrawerOpen(true)
  }

  const openEditDrawer = async (item: OfferItem) => {
    setDrawerMode('edit')
    setDrawerOpen(true)
    setProductForm(mapProductDetailToForm(item, item.type))
    await loadProductIntoDrawer(item.id, item)
  }

  /*
   * Deep link desde el dashboard Lite:
   * /offers?editProductId=857
   * Cambia a gestión y abre el drawer del producto correcto.
   */
  useEffect(() => {
    if (!isOwner || isLoading || isUserLoading || isSellerLoading || !sellerId) return

    const url = new URL(window.location.href)
    const productId = Number(url.searchParams.get('editProductId') || 0)
    if (!Number.isSafeInteger(productId) || productId <= 0) return

    const item = items.find(offer => offer.id === productId)
    if (!item) return

    url.searchParams.delete('editProductId')
    window.history.replaceState(
      window.history.state,
      '',
      `${url.pathname}${url.search}${url.hash}`
    )

    setEditMode(true)
    void openEditDrawer(item)
    // Esperamos que /api/offers haya cargado antes de abrir.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOwner, isLoading, isUserLoading, isSellerLoading, sellerId, items])

  const handleSaveDrawerProduct = async () => {
    if (!sellerId) return

    setDrawerSaving(true)

    try {
      const isEdit = drawerMode === 'edit' && productForm.productId
      const url = isEdit ? `/api/offers/${productForm.productId}` : '/api/product'
      const method = isEdit ? 'PATCH' : 'POST'

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(mapFormToProductPayload(productForm, sellerId)),
      })

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        throw new Error(data?.error || 'No se pudo guardar el producto')
      }

      const savedProductId = Number(data?.productId || data?.product_id || productForm.productId || 0)

      setDrawerOpen(false)
      setShowNewModal(false)
      setNewOfferName('')
      setSelectedType(null)

      await loadOffers()

      if (savedProductId) {
        setProductForm((prev) => ({ ...prev, productId: savedProductId }))
      }
    } catch (err) {
      console.error('[offers.drawer.save]', err)
    } finally {
      setDrawerSaving(false)
    }
  }

  const handleOpenNewModal = () => {
    setShowNewModal(true)
    setSelectedType(null)
    setNewOfferName('')
  }

  const handleCreateOffer = async () => {
    if (!selectedType || !newOfferName.trim()) return

    const preset = productTypePresets.find(
      (p) => p.id === selectedType && p.enabled !== false
    )

    if (!preset) return

    const pricingType = preset.defaultPricingType || 'one_time'

    setShowNewModal(false)

    setDrawerMode('create')
    setProductForm({
      ...makeDefaultProductForm(selectedType),
      productType: selectedType,
      pricingType,
      type: selectedType,
      name: newOfferName.trim(),
    })

    setDrawerOpen(true)
  }

  const handleToggle = async (item: OfferItem, next: boolean) => {
    if (!sellerId) return

    const prev = items
    setItems((current) => current.map((it) => (it.id === item.id ? { ...it, status: next ? 1 : 0 } : it)))

    try {
      const res = await fetch(`/api/offers/${item.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sellerId,
          status: next ? 1 : 0,
        }),
      })

      if (!res.ok) throw new Error('No se pudo actualizar el estado')
    } catch (err) {
      console.error('[offers.toggle]', err)
      setItems(prev)
    }
  }

  const handleCopyLink = async (item: OfferItem) => {
    try {
      const origin = window.location.origin
      const url = `${origin}/product/${item.id}`
      await navigator.clipboard.writeText(url)
    } catch (err) {
      console.error('[offers.copyLink]', err)
    }
  }

  const handleDeleteProduct = async () => {
    if (!productToDelete || !isOwner || deletingProduct) return

    const product = productToDelete
    setDeletingProduct(true)

    try {
      const res = await fetch(`/api/offers/${product.id}`, {
        method: 'DELETE',
      })

      const data = await res.json().catch(() => null)

      if (!res.ok || !data?.ok) {
        throw new Error(data?.error || 'No se pudo eliminar')
      }

      setItems(current => current.filter(item => item.id !== product.id))
      setProductToDelete(null)

      toast.success('Producto eliminado correctamente')
    } catch (error) {
      console.error('[offers.delete]', error)
      toast.error('No pudimos eliminar el producto. Inténtalo nuevamente.')
    } finally {
      setDeletingProduct(false)
    }
  }
  
  const handleDuplicate = async (item: OfferItem): Promise<boolean> => {
    if (!sellerId) return false

    try {
      const res = await fetch('/api/offers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sellerId,
          name: `${item.name} (copia)`,
          type: item.type,
          duplicateFromProductId: item.id,
        }),
      })

      const data = await res.json().catch(() => null)

      if (!res.ok || !data?.success) {
        throw new Error(data?.error || 'No se pudo duplicar la oferta')
      }

      const productId = Number(data?.productId || 0)

      if (!productId) {
        throw new Error('No se recibió el ID del producto duplicado')
      }

      await loadOffers()

      const duplicatedItem: OfferItem = {
        ...item,
        id: productId,
        name: `${item.name} (copia)`,
        isPublished: 0,
        status: 0,
      }

      setProductToClone(null)

      toast.success('Producto duplicado correctamente')

      await openEditDrawer(duplicatedItem)

      return true
    } catch (err) {
      console.error('[offers.duplicate]', err)
      toast.error('No pudimos duplicar el producto. Inténtalo nuevamente.')
      return false
    }
  }

  const handleEdit = async (item: OfferItem) => {
    await openEditDrawer(item)
  }

  const canEditOffers = isOwner

  const headerTitle = editMode
    ? 'Gestionar ofertas'
    : 'Productos digitales y ofertas'

  const headerDescription = editMode
    ? 'Administra tus ofertas, accesos y productos digitales.'
    : 'Explora productos, recursos y ofertas disponibles.'

  if (isLoading || isUserLoading || isSellerLoading) {
    return (
      <div className="h-full min-h-0 overflow-hidden px-4 pb-4 md:px-6 md:pb-6">
        <div className="mx-auto h-full min-h-0 w-full max-w-[1520px]">
          <div className="hubee-premium-content h-full min-h-0 overflow-y-auto p-6">
            <div className="grid gap-4 md:grid-cols-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <Card key={i}>
                  <CardContent className="p-5">
                    <div className="animate-pulse space-y-4">
                      <div className="flex gap-4">
                        <div className="h-16 w-16 rounded-2xl bg-muted" />
                        <div className="flex-1 space-y-2">
                          <div className="h-4 w-40 rounded bg-muted" />
                          <div className="h-3 w-full rounded bg-muted" />
                          <div className="h-3 w-2/3 rounded bg-muted" />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                        {Array.from({ length: 4 }).map((__, j) => (
                          <div key={j} className="h-20 rounded-2xl bg-muted" />
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      {/* HEADER LIBRE */}
      <div className="shrink-0 px-4 pb-4 pt-1 md:px-6 md:pb-5 md:pt-2">
        <div className="mx-auto w-full max-w-[1520px]">
          <div className="flex flex-col justify-between gap-3 md:flex-row md:items-end">
            <div>
              {canEditOffers && editMode ? (
                <button
                  onClick={() => setEditMode(false)}
                  className="mb-2 inline-flex items-center gap-2 rounded-full bg-background/55 px-3 py-1.5 text-sm font-medium text-muted-foreground ring-1 ring-border/35 transition hover:bg-background/75 hover:text-foreground"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Volver a vista pública
                </button>
              ) : null}

              <h1 className="flex items-center gap-3 text-[26px] font-medium leading-tight tracking-[-0.035em] text-foreground/92 md:text-[32px]">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary ring-1 ring-primary/15">
                  <Package className="h-5 w-5" />
                </span>
                {canEditOffers ? headerTitle : 'Productos digitales y ofertas'}
              </h1>

              <p className="mt-2 max-w-2xl text-sm font-normal leading-5 text-muted-foreground">
                {canEditOffers
                  ? headerDescription
                  : 'Recursos, accesos y experiencias digitales disponibles.'}
              </p>
            </div>
            
            <div className="flex w-full flex-wrap items-center gap-2 md:w-auto md:justify-end">
              {canEditOffers && !editMode && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="inline-flex min-w-0 flex-1 items-center justify-center gap-1.5 rounded-full bg-background/55 ring-1 ring-border/35 backdrop-blur-xl md:flex-none"
                  onClick={() => setEditMode(true)}
                >
                  <Pencil className="h-4 w-4 shrink-0" />
                  Editar
                </Button>
              )}

              {canEditOffers && (
                <Button
                  type="button"
                  size="sm"
                  onClick={handleOpenNewModal}
                  className="min-w-0 flex-1 gap-2 rounded-full md:flex-none"
                >
                  <Plus className="h-4 w-4 shrink-0" />
                  Nueva oferta
                </Button>
              )}
            </div>

          </div>
        </div>
      </div>

      {/* PREMIUM CARD PRINCIPAL */}
      <div className="min-h-0 flex-1 px-4 pb-4 md:px-6 md:pb-6">
        <div className="mx-auto h-full min-h-0 w-full max-w-[1520px]">
          <div className="flex h-full min-h-0 flex-col overflow-hidden">
            {editMode && canEditOffers ? (
              <>
                {/* STATS + TOOLBAR EDIT MODE */}
                <div className="shrink-0 border-b border-border/25 bg-background/18 px-5 py-4 backdrop-blur-2xl md:px-6">
                  <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                    <div className="rounded-2xl border border-border/35 bg-background/45 p-3 shadow-[0_12px_38px_hsl(var(--foreground)/0.04)] backdrop-blur-xl">
                      <div className="text-xs font-normal text-muted-foreground">Total</div>
                      <div className="mt-1 text-xl font-medium text-foreground/90">
                        {stats.total}
                      </div>
                    </div>

                    <div className="rounded-2xl border border-border/35 bg-background/45 p-3 shadow-[0_12px_38px_hsl(var(--foreground)/0.04)] backdrop-blur-xl">
                      <div className="text-xs font-normal text-muted-foreground">Activas</div>
                      <div className="mt-1 text-xl font-medium text-foreground/90">
                        {stats.active}
                      </div>
                    </div>

                    <div className="rounded-2xl border border-border/35 bg-background/45 p-3 shadow-[0_12px_38px_hsl(var(--foreground)/0.04)] backdrop-blur-xl">
                      <div className="text-xs font-normal text-muted-foreground">Inactivas</div>
                      <div className="mt-1 text-xl font-medium text-foreground/90">
                        {stats.inactive}
                      </div>
                    </div>

                    <div className="rounded-2xl border border-border/35 bg-background/45 p-3 shadow-[0_12px_38px_hsl(var(--foreground)/0.04)] backdrop-blur-xl">
                      <div className="text-xs font-normal text-muted-foreground">Ingresos</div>
                      <div className="mt-1 text-sm font-medium text-foreground/90">
                        {money(stats.revenue, sellerCurrency)}
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                    <div className="relative w-full max-w-xl">
                      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Buscar por nombre o modelo..."
                        className="h-11 rounded-full border-border/35 bg-background/50 pl-10 shadow-[0_10px_30px_hsl(var(--foreground)/0.04)] backdrop-blur-xl focus-visible:ring-primary/20"
                      />
                    </div>

                    <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
                      {ownerFilters.map((opt) => {
                        const active = filter === opt.id

                        return (
                          <button
                            key={opt.id}
                            type="button"
                            onClick={() => setFilter(opt.id)}
                            className={cn(
                              'whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium backdrop-blur-xl transition-all',
                              active
                                ? 'bg-primary text-primary-foreground'
                                : 'border border-border/35 bg-background/45 text-muted-foreground hover:bg-background/65 hover:text-foreground'
                            )}
                          >
                            {opt.label}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                </div>

                {/* LISTADO EDIT MODE */}
                <div
                  className="min-h-0 flex-1 overflow-y-auto px-5 pb-6 pt-5 md:px-6 md:pb-7"
                  style={{
                    WebkitMaskImage:
                      'linear-gradient(to bottom, transparent 0px, black 26px, black calc(100% - 18px), transparent 100%)',
                    maskImage:
                      'linear-gradient(to bottom, transparent 0px, black 26px, black calc(100% - 18px), transparent 100%)',
                  }}
                >
                  {items.length === 0 ? (
                    <Card className="rounded-[1.75rem] border border-border/35 bg-background/55 shadow-[0_12px_38px_hsl(var(--foreground)/0.045)]">
                      <CardContent className="px-6 py-16 text-center">
                        <div className="mb-4 inline-flex rounded-3xl bg-primary/10 p-4 text-primary">
                          <Package className="h-8 w-8" />
                        </div>
                        <h2 className="text-[18px] font-medium tracking-[-0.02em] text-foreground/90">
                          Aún no tienes ofertas
                        </h2>
                        <p className="mt-2 text-sm font-normal text-muted-foreground">
                          Crea tu primera oferta para vender accesos únicos, membresías, eventos o productos digitales.
                        </p>
                        <Button onClick={handleOpenNewModal} className="mt-6 rounded-full">
                          <Plus className="mr-2 h-4 w-4" />
                          Crear primera oferta
                        </Button>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="space-y-3">
                      {items.map((p) => (
                        <ProductListRow
                          key={p.id}
                          product={p}
                          onToggle={handleToggle}
                          onEdit={handleEdit}
                          onDuplicate={setProductToClone}
                          onDelete={setProductToDelete}
                          onCopyLink={handleCopyLink}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                {/* TOOLBAR PUBLIC MODE */}
                <div className="shrink-0">
                  <div className="flex items-center gap-2">
                    <div className="flex flex-1 gap-2 overflow-x-auto pb-1 scrollbar-hide">
                      {publicCategories.map((cat, i) => {
                        const active =
                          publicCategory === cat ||
                          (i === 0 && publicCategory === 'Todos')

                        return (
                          <button
                            key={cat}
                            onClick={() => setPublicCategory(cat)}
                            className={cn(
                              'whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium backdrop-blur-xl transition-all',
                              active
                                ? 'bg-primary text-primary-foreground'
                                : 'border border-border/35 bg-background/45 text-muted-foreground hover:bg-background/65 hover:text-foreground'
                            )}
                          >
                            {cat}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                </div>

                {/* LISTADO PUBLIC MODE */}
                <div
                  className="min-h-0 flex-1 overflow-y-auto pb-6 pt-5 md:pb-7"
                  style={{
                    WebkitMaskImage:
                      'linear-gradient(to bottom, transparent 0px, black 26px, black calc(100% - 18px), transparent 100%)',
                    maskImage:
                      'linear-gradient(to bottom, transparent 0px, black 26px, black calc(100% - 18px), transparent 100%)',
                  }}
                >
                  <div className="space-y-6">
                    {promoProduct ? <PromoCard product={promoProduct} /> : null}

                    {!promoProduct && displayedProducts.length === 0 ? (
                      <Card className="rounded-[1.75rem] border border-border/35 bg-background/55 shadow-[0_12px_38px_hsl(var(--foreground)/0.045)]">
                        <CardContent className="px-6 py-16 text-center">
                          <div className="mb-4 inline-flex rounded-3xl bg-primary/10 p-4 text-primary">
                            <Package className="h-8 w-8" />
                          </div>
                          <h2 className="text-[18px] font-medium tracking-[-0.02em] text-foreground/90">
                            {canEditOffers
                              ? 'Aún no hay ofertas públicas'
                              : 'No hay ofertas disponibles'}
                          </h2>
                          <p className="mt-2 text-sm font-normal text-muted-foreground">
                            {canEditOffers
                              ? 'Cuando actives tus ofertas aparecerán aquí para tus visitantes.'
                              : 'Vuelve pronto para ver nuevos accesos, eventos y productos digitales.'}
                          </p>
                        </CardContent>
                      </Card>
                    ) : displayedProducts.length > 0 ? (
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-6 lg:grid-cols-4">
                        {displayedProducts.map((p) => (
                          <ProductCard key={p.id} product={p} />
                        ))}
                      </div>
                    ) : null}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <ProductDrawer
        open={drawerOpen}
        mode={drawerMode}
        form={productForm}
        setForm={setProductForm}
        loading={drawerLoading}
        saving={drawerSaving}
        onClose={() => setDrawerOpen(false)}
        onSave={handleSaveDrawerProduct}
        sellerId={sellerId}
        affiliatesEnabled={Boolean(features?.affiliate_invites)}
        affiliateSchemes={affiliateSchemes}
      />

      {/* CONFIRMAR CLONACIÓN */}
      <Dialog
        open={Boolean(productToClone)}
        onOpenChange={(open) => {
          if (!open && !cloningProduct) setProductToClone(null)
        }}
      >
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Copy className="h-5 w-5 text-primary" />
              ¿Duplicar este producto?
            </DialogTitle>

            <DialogDescription className="pt-2 leading-relaxed">
              Crearemos una copia de{' '}
              <strong>{productToClone?.name}</strong> para que puedas
              personalizarla sin modificar el producto original.
              La copia comenzará como borrador y podrás revisarla antes
              de ponerla a la venta.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              disabled={cloningProduct}
              onClick={() => setProductToClone(null)}
            >
              Cancelar
            </Button>

            <Button
              disabled={cloningProduct}
              onClick={async () => {
                if (!productToClone || cloningProduct) return

                setCloningProduct(true)

                try {
                  const success = await handleDuplicate(productToClone)

                  if (success) {
                    setProductToClone(null)
                  }
                } finally {
                  setCloningProduct(false)
                }
              }}
            >
              {cloningProduct ? 'Duplicando...' : 'Duplicar producto'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CONFIRMAR ELIMINACIÓN */}
      <Dialog
        open={Boolean(productToDelete)}
        onOpenChange={(open) => {
          if (!open && !deletingProduct) setProductToDelete(null)
        }}
      >
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              ¿Eliminar este producto?
            </DialogTitle>

            <DialogDescription className="pt-2 leading-relaxed">
              <strong>{productToDelete?.name}</strong> dejará de aparecer
              en tu catálogo y no estará disponible para nuevas compras.
              Conservaremos su historial de ventas y la información
              relacionada.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              disabled={deletingProduct}
              onClick={() => setProductToDelete(null)}
            >
              Cancelar
            </Button>

            <Button
              variant="destructive"
              disabled={deletingProduct}
              onClick={handleDeleteProduct}
            >
              {deletingProduct ? 'Eliminando...' : 'Eliminar producto'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showNewModal} onOpenChange={setShowNewModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Crear nuevo producto</DialogTitle>
            <DialogDescription>
              Elige qué tipo de producto vas a crear. Después podrás configurar precio, accesos, promoción y disponibilidad.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-2">
            <div className="space-y-2">
              <Label className="text-sm font-medium">¿Qué quieres crear?</Label>
              <div className="grid grid-cols-1 gap-2">
                {productTypePresets.filter((preset) => preset.enabled !== false).map((preset) => {
                  const Icon = preset.icon
                  const isSelected = selectedType === preset.id

                  return (
                    <button
                      key={preset.id}
                      type="button"
                      onClick={() => setSelectedType(preset.id)}
                      className={`flex items-start gap-3 rounded-xl border p-3 text-left transition-all ${
                        isSelected
                          ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
                          : 'border-border hover:border-muted-foreground/30 hover:bg-muted/50'
                      }`}
                    >
                      <div
                        className={`mt-0.5 rounded-lg p-2 ${
                          isSelected
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                      </div>

                      <div className="min-w-0">
                        <span
                          className={`block text-sm font-semibold ${
                            isSelected ? 'text-primary' : 'text-foreground'
                          }`}
                        >
                          {preset.title}
                        </span>
                        <span className="mt-0.5 block text-xs leading-tight text-muted-foreground">
                          {preset.description}
                        </span>
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="offer-name">Nombre de la oferta</Label>
              <Input
                id="offer-name"
                placeholder="Ej: Reto Confianza en Movimiento"
                value={newOfferName}
                onChange={(e) => setNewOfferName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreateOffer()}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreateOffer} disabled={!newOfferName.trim() || !selectedType || isSubmitting}>
              {isSubmitting ? 'Creando...' : 'Continuar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}